import { Component, OnInit, Output, EventEmitter, ElementRef, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { InquiryService } from 'src/app/services/inquiry/inquiry.service';
import { GeneralService } from 'src/app/services/general/general.service';

@Component({
  selector: 'app-send-inquiry-reply',
  templateUrl: './send-inquiry-reply.component.html',
  styleUrls: ['./send-inquiry-reply.component.scss']
})
export class SendInquiryReplyComponent extends DestructorComponent implements OnInit {

  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  @Output() replyAdded = new EventEmitter<void>();

  replyForm!: FormGroup;
  selectedInquiry: any = null;
  isLoading = false;

  // File upload properties
  selectedFile: File | null = null;
  uploadedFileUrl: string = '';
  isUploading = false;

  // Dummy message options
  messageOptions = [
    { value: 'greeting', label: 'Thank you for your inquiry' },
    { value: 'availability', label: 'Vehicle availability confirmation' },
    { value: 'pricing', label: 'Price quotation' },
    { value: 'shipping', label: 'Shipping information' },
    { value: 'documentation', label: 'Documentation requirements' },
    { value: 'custom', label: 'Custom message' }
  ];

  constructor(
    private fb: FormBuilder,
    private _inquiryService: InquiryService,
    private _generalService: GeneralService
  ) {
    super();
    this.initializeForm();
  }

  ngOnInit(): void {
    this.setupModalListener();
  }

  private initializeForm(): void {
    this.replyForm = this.fb.group({
      select_message: ['', [Validators.required]],
      subject: ['', [Validators.required, Validators.minLength(3)]],
      message: ['', [Validators.required]],
      file_url: [''],
      inquiry_id: ['']
    });
  }

  // Getter methods for form controls
  get select_message() { return this.replyForm.get('select_message'); }
  get subject() { return this.replyForm.get('subject'); }
  get message() { return this.replyForm.get('message'); }
  get file_url() { return this.replyForm.get('file_url'); }
  get inquiry_id() { return this.replyForm.get('inquiry_id'); }

  private setupModalListener(): void {
    const modalElement = document.getElementById('kt_modal_send_inquiry_reply');
    if (modalElement) {
      modalElement.addEventListener('shown.bs.modal', () => {
        const selectedInquiry = (window as any).selectedInquiryForReply;
        if (selectedInquiry) {
          this.setSelectedInquiry(selectedInquiry);
        }
      });

      modalElement.addEventListener('hidden.bs.modal', () => {
        this.resetForm();
      });
    }
  }

  setSelectedInquiry(inquiry: any): void {
    this.selectedInquiry = inquiry;
    this.replyForm.patchValue({
      inquiry_id: inquiry._id
    });
  }

  private resetForm(): void {
    this.replyForm.reset({
      select_message: '',
      subject: '',
      message: '',
      file_url: '',
      inquiry_id: ''
    });
    this.selectedFile = null;
    this.uploadedFileUrl = '';
    this.isUploading = false;
    this.selectedInquiry = null;
    this.markFormGroupUntouched(this.replyForm);
  }

  private markFormGroupUntouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsUntouched();
      if (control instanceof FormGroup) {
        this.markFormGroupUntouched(control);
      }
    });
  }

  // File upload methods
  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.selectedFile = input.files[0];
      this.uploadFile();
    }
  }

  removeFile(): void {
    this.selectedFile = null;
    this.uploadedFileUrl = '';
    this.replyForm.patchValue({ file_url: '' });

    // Reset file input
    if (this.fileInput?.nativeElement) {
      this.fileInput.nativeElement.value = '';
    }
  }

  private uploadFile(): void {
    if (!this.selectedFile) return;

    this.isUploading = true;

    this.subscriptions.push(
      this._generalService.uploadFileByContent({ "contentType": this.selectedFile.type }).subscribe({
        next: (res) => {
          const fileUrl = res.data.url;
          const formData = this._generalService.convertKeysToFormdata(res.data.result.fields, this.selectedFile);

          this.subscriptions.push(
            this._generalService.sendFileToS3(res.data.result.url, formData).subscribe({
              next: () => {
                this.uploadedFileUrl = fileUrl;
                this.replyForm.patchValue({ file_url: fileUrl });
                this.isUploading = false;
              },
              error: (error) => {
                console.error('Error uploading file to S3:', error);
                this.isUploading = false;
              }
            })
          );
        },
        error: (error) => {
          console.error('Error getting presigned URL:', error);
          this.isUploading = false;
        }
      })
    );
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  onSubmit(): void {
    if (this.replyForm.valid && !this.isLoading) {
      this.isLoading = true;

      const formData = this.replyForm.value;

      // Prepare reply object
      const replyData = {
        reply: {
          select_message: formData.select_message,
          subject: formData.subject,
          message: formData.message,
          file_url: formData.file_url || '',
          inquiry_id: formData.inquiry_id
        }
      };

      this.subscriptions.push(
        this._inquiryService.UpdateData(this.selectedInquiry._id, replyData).subscribe({
          next: (response: any) => {
            this.isLoading = false;
            if (response.status) {
              console.log('Reply sent successfully!');
              this.replyAdded.emit();
              this.closeModal();
            } else {
              console.error('Failed to send reply:', response.message);
            }
          },
          error: (error) => {
            console.error('Error sending reply:', error);
            this.isLoading = false;
          }
        })
      );
    } else {
      // Mark all fields as touched to show validation errors
      this.markFormGroupTouched(this.replyForm);
    }
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  private closeModal(): void {
    const modalElement = document.getElementById('kt_modal_send_inquiry_reply');
    if (modalElement) {
      const modal = (window as any).bootstrap.Modal.getInstance(modalElement);
      if (modal) {
        modal.hide();
      }
    }
  }
}
